import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { DEFAULT_BIG_NUDGE, looksEqual, doFormatterSafeParse } from '../formatter/formatter.utils.js';
import { useScrub } from '../hooks/use-scrub/use-scrub.js';
import { useHaptics } from '../settings/settings.haptics.js';
import { focusElement, isFocusWithin } from '../utils/focus.js';
import { take } from '../utils/ref.js';
import { ScrubCursor } from './scrubbable.components.js';
import { cursorWidthForMultiplier, scaleForScrubEvent, increment } from './scrubbable.utils.js';
import { trigger } from './scrubbable.css.js';

/** How much to multiply the scrub delta by when applying increments. */
const DEFAULT_MULTIPLIER = 0.5;
/**
 * Get a scrubbable manager configured with a `onChange` handler. The returned `manager` must be
 * passed to the `Scrubbable.Root` component. The manager can be used to determine if the user is
 * currently scrubbing.
 */
function useScrubbable({ value, getStringValue, formatter, multiplier = DEFAULT_MULTIPLIER, disabled, onChange, onChangeRestricted, onCancel, onScrubScaleChange, }) {
    const haptics = useHaptics();
    const start = useRef(null);
    const delta = useRef(0);
    const mixedDeltas = useRef(new Map());
    const lastScale = useRef(null);
    const lastFocus = useRef(null);
    const didChange = useRef(false);
    const scrubScaleChangeCallback = useRef(null);
    const parseCallbackRef = useRef(null);
    const lastTrigger = useRef(0);
    const bigNudge = formatter.getNudgeAmount?.(true, value) || DEFAULT_BIG_NUDGE;
    const setScrubScaleChangeCallback = useCallback((callback) => {
        scrubScaleChangeCallback.current = callback;
    }, []);
    const { scrubbing, getScrubProps, Cursor } = useScrub({
        disabled,
        singleAxis: 'x',
        onBeforeScrub(e) {
            if (e.target.classList.contains(trigger)) {
                return true;
            }
            // alt-pointer, middle, and wheel should all scrub unconditionally
            return e.source !== 'pointer';
        },
        onScrubStart(e) {
            if (isFocusWithin(e.target)) {
                lastFocus.current = document.activeElement;
                lastFocus.current.blur();
            }
            if (getStringValue) {
                const valueStr = getStringValue();
                const res = doFormatterSafeParse(formatter, valueStr, value, 'scrub', null);
                if (res) {
                    if (res.callback) {
                        parseCallbackRef.current = res.callback;
                    }
                    else {
                        start.current = res.value ?? value;
                    }
                }
                else {
                    start.current = null;
                }
            }
            else {
                start.current = value;
            }
            delta.current = 0;
            mixedDeltas.current.clear();
            lastScale.current = null;
            didChange.current = false;
        },
        onScrub(e) {
            const [scale, d] = scaleForScrubEvent(e);
            if (scale !== lastScale.current) {
                if (lastScale.current != null) {
                    onScrubScaleChange?.(scale, d);
                    scrubScaleChangeCallback.current?.(scale, d);
                }
                lastScale.current = scale;
            }
            // The scrub delta is cumulative, changing the scrub scale doesn't immediately change the
            // delta, but does change the size of the subsequent increments.
            const movement = e.movement.x * scale * multiplier;
            if (parseCallbackRef.current) {
                // Each starting value needs its own delta so clamping one doesn't affect the others.
                // Read the previous event's deltas so repeated values receive the same increment too.
                const previousDeltas = mixedDeltas.current;
                const nextDeltas = new Map();
                const update = (newValue) => {
                    const valueDelta = { current: previousDeltas.get(newValue) ?? 0 };
                    const result = increment(formatter, newValue, movement, valueDelta, e.shiftKey);
                    nextDeltas.set(newValue, valueDelta.current);
                    return result.value;
                };
                parseCallbackRef.current(update, { commit: false });
                mixedDeltas.current = nextDeltas;
            }
            else if (start.current != null) {
                const newValue = increment(formatter, start.current, movement, delta, e.shiftKey);
                if (!looksEqual(formatter, newValue.value, value)) {
                    onChange(newValue.value, Object.assign(e, { commit: false }));
                    didChange.current = true;
                    // fire haptics if we’ve traveled enough distance since the last trigger
                    // lastTrigger is needed because if a user is scrubbing fast, they may not
                    // land on an exact pixel—it could be 2px above, etc. This just ensures we don’t skip triggers.
                    if (haptics && typeof newValue.value === 'number') {
                        if (Math.abs(newValue.value - lastTrigger.current) >= bigNudge) {
                            haptics.trigger('generic');
                            lastTrigger.current = newValue.value - (newValue.value % bigNudge); // snap to bigNudge value
                        }
                    }
                }
                if (newValue.value !== newValue.preClamped) {
                    onChangeRestricted?.(newValue.preClamped, { value: newValue.value });
                }
            }
        },
        onScrubEnd(e) {
            const parseCallback = take(parseCallbackRef);
            if (e.cancelled) {
                if (parseCallback) {
                    parseCallback((newValue) => newValue, { commit: true, cancelled: true });
                }
                else if (start.current != null) {
                    onCancel(start.current, e);
                }
            }
            else {
                const update = (newValue) => {
                    const valueDelta = parseCallback
                        ? { current: mixedDeltas.current.get(newValue) ?? 0 }
                        : delta;
                    return increment(formatter, newValue, 0, valueDelta, e.shiftKey).value;
                };
                if (parseCallback) {
                    parseCallback(update, { commit: true });
                }
                else if (start.current != null) {
                    const newValue = update(start.current);
                    if (didChange.current || !looksEqual(formatter, newValue, value)) {
                        onChange(newValue, Object.assign(e, { commit: true }));
                    }
                }
            }
            if (lastFocus.current) {
                const el = take(lastFocus);
                // Allow the onChange to finish before focusing, otherwise the value change might cause
                // the input's text to be deselected.
                setTimeout(() => focusElement(el));
            }
        },
    });
    return {
        scrubbing,
        private: { getScrubProps, Cursor, setScrubScaleChangeCallback },
    };
}
/**
 * A container that can be scrubbed.
 *
 * @see https://fpl.figma.design/components/scrubbable/
 */
const Root = forwardRef(({ manager, ...props }, ref) => {
    const [scale, setScale] = useState(1);
    const privateFields = manager?.private;
    const getScrubProps = privateFields?.getScrubProps;
    const Cursor = privateFields?.Cursor;
    const setScrubScaleChangeCallback = privateFields?.setScrubScaleChangeCallback;
    useEffect(() => setScrubScaleChangeCallback?.((_, nextScale) => setScale(nextScale)), [setScrubScaleChangeCallback]);
    const width = cursorWidthForMultiplier(scale);
    return (jsxs(Fragment, { children: [jsx("div", { "data-fpl-component": "", ref: ref, ...(getScrubProps?.(props) ?? props) }), Cursor && (jsx(Cursor, { children: jsx(ScrubCursor, { width: width }) }))] }));
});
Root.displayName = 'Scrubbable.Root';
/**
 * An explicit region inside of Scrubbable.Root that is scrubbable without modifiers.
 *
 * Always marked aria-hidden so should only contain decorative content or be empty.
 */
const Trigger = forwardRef(({ className, ...props }, ref) => (jsx("div", { "data-fpl-component": "", ...props, ref: ref, className: clsx(trigger, className), "aria-hidden": true })));
Trigger.displayName = 'Scrubbable.Trigger';

export { Root, Trigger, useScrubbable };
//# sourceMappingURL=scrubbable.js.map
